import {useRuleCreate} from "@hooks/rule.ts";
import React, {FC, useCallback, useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Button} from "@components/ui/button.tsx";
import {Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle} from "@components/ui/dialog";
import {Input} from "@components/ui/input";
import {Plus} from "lucide-react";
import {ActionType, Condition, DEFAULT_COMPOSED_PUMP_FUN_QUICK, DEFAULT_COMPOSED_PUMP_UP_QUICK, RuleType} from "@types";

export type CreateRuleProps = {
    title?: String;
    ruleType: RuleType;
}

export const CreateRuleButton: FC<CreateRuleProps> = React.memo(({title, ruleType}) => {
    const [createdRule, loading, error, createRule] = useRuleCreate();
    const abortControllerRef = useRef<AbortController | null>(null);
    const navigate = useNavigate();

    const [open, setOpen] = useState(false);
    const [ruleName, setRuleName] = useState("");

    const handleCreateClick = useCallback(() => {
        setOpen(true);
    }, []);

    const handleConfirmCreate = () => {
        if (!ruleName.trim()) return;

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const newAbortController = new AbortController();
        abortControllerRef.current = newAbortController;

        createRule({
            name: ruleName,
            rule: ruleType,
            sequence: {
                condition: resolveDefaultCondition(ruleType),
                action: {
                    type: ActionType.NOTIFY_TELEGRAM,
                    buttons: Array(6).fill({
                        action: 'NOTHING',
                        value: undefined
                    })
                }
            }
        }, newAbortController);

        setOpen(false);
        setRuleName("");
    };

    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    useEffect(() => {
        if (createdRule) {
            navigate(`/automate/rules/${createdRule.id}`);
        }
    }, [createdRule]);

    const handleClose = useCallback(() => {
        setRuleName('');
        setOpen(false);
    }, []);

    return (
        <>
            <Button
                onClick={handleCreateClick}
                disabled={loading}>
                <Plus className="w-6 h-6"/> {title || 'Add Rule'}
            </Button>

            <Dialog open={open} onOpenChange={(open) => {
                if (!open) {
                    handleClose()
                }
            }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className={"text-zinc-500"}>Name your rule</DialogTitle>
                    </DialogHeader>
                    <Input
                        type="text"
                        placeholder="Name of your rule"
                        value={ruleName}
                        onChange={(e) => setRuleName(e.target.value)}
                        className="mt-2"
                    />
                    <DialogFooter className="flex flex-row justify-end gap-2">
                        <Button variant="outline" onClick={handleClose}>Cancel</Button>
                        <Button variant="default" onClick={handleConfirmCreate} disabled={!ruleName.trim()}>
                            Create
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
});

const resolveDefaultCondition = (type: RuleType): Condition => {
    switch (type) {
        case RuleType.PUMP_FUN:
            return {...DEFAULT_COMPOSED_PUMP_FUN_QUICK} as unknown as Condition;
        case RuleType.PUMP_FUN_MANAGED:
            break;
        case RuleType.PUMP_SWAP:
            break;
        case RuleType.PUMP_SWAP_MANAGED:
            break;
        case RuleType.PUMP_UP:
            return {...DEFAULT_COMPOSED_PUMP_UP_QUICK} as unknown as Condition;
        case RuleType.PUMP_UP_MANAGED:
            break;
        case RuleType.JUPITER:
            break;
        case RuleType.JUPITER_MANAGED:
            break;
    }

    throw Error(`Can not resolve default condition for ${type}`);
}